import { graphql, useStaticQuery } from "gatsby";
import PropTypes from "prop-types";
import React, { useMemo } from "react";

import { menuFilter } from "../utils/menuFilter";
import Footer from "./footer";
import Nav from "./nav";

const Layout = (props) => {
  const {
    children,
    containerClass,
    headerContainerClass,
    bgImage = null,
    footerShown = true,
    brandingOpts,
    isGroupPage,
    applyNow = "/application-form/",
    applyLabel = "Apply",
    enquireNow = "/enquiry-form/",
    enquireLabel = "Enquire",
    programPrice,
    showProgramPrice,
    showPricingStickyWidget = false,
    programType = [],
    showPromotion = false,
    programPromotion = "",
    updateOnScroll = true,
    stickyFooterDetails = {},
    showMobileEnquireNow = true,
    showMobileApplyNow = true,
    showMobileStickyFooter = true,
    showGrantsBanner = true,
    additionalFooterOpts = {}
  } = props;

  const { genericDetails, site, _primarySource } = useStaticQuery(graphql`
    {
      genericDetails: wp {
        parentInfo: acfOptionsParentInfo {
          parentInfo: acfParentInfo {
            ...ParentInfoFragment
          }
        }
      }

      site {
        siteMetadata {
          description
          site_url
          nav_title
          sec_nav_title
          title
          author
          theme
          brochures {
            brochure_dl
            travel_dl
            parent_dl
          }
          social_media {
            blog
            facebook
            instagram
            linkedin
            tiktok
            twitter
            whatsapp
            youtube
            google_scholar
          }
        }
      }

      _primarySource {
        defaultHeaderMenu: menus(where: { language: "en", location: PRIMARY }) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }
        defaultFooterMenu: menus(
          where: { language: "en", location: MAIN_FOOTER }
        ) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }

        headerMenu_US: menus(where: { language: "us", location: PRIMARY }) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }
        footerMenu_US: menus(where: { language: "us", location: MAIN_FOOTER }) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }

        headerMenu_ZA: menus(where: { language: "za", location: PRIMARY }) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }
        footerMenu_ZA: menus(where: { language: "za", location: MAIN_FOOTER }) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }

        headerMenu_IE: menus(where: { language: "ie", location: PRIMARY }) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }
        footerMenu_IE: menus(where: { language: "ie", location: MAIN_FOOTER }) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }

        headerMenu_CA: menus(where: { language: "can", location: PRIMARY }) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }
        footerMenu_CA: menus(
          where: { language: "can", location: MAIN_FOOTER }
        ) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }

        headerMenu_AU: menus(where: { language: "au", location: PRIMARY }) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }
        footerMenu_AU: menus(where: { language: "au", location: MAIN_FOOTER }) {
          nodes {
            ..._PrimarySource_MenuFragment
          }
        }
      }
    }
  `);

  const {
    defaultFooterMenu,
    defaultHeaderMenu,

    headerMenu_US,
    headerMenu_IE,
    headerMenu_AU,
    headerMenu_CA,
    headerMenu_ZA,

    footerMenu_US,
    footerMenu_IE,
    footerMenu_AU,
    footerMenu_CA,
    footerMenu_ZA
  } = _primarySource;

  let footerMenu, headerMenu;

  const siteLocaleEnv = process.env.GATSBY_SITE_LOCALE ?? "en_GB";

  switch (siteLocaleEnv) {
    case "us":
    case "en_US":
      footerMenu = menuFilter(footerMenu_US, "MAIN_FOOTER");
      headerMenu = menuFilter(headerMenu_US, "PRIMARY");
      break;

    case "ie":
    case "en_IE":
      footerMenu = menuFilter(footerMenu_IE, "MAIN_FOOTER");
      headerMenu = menuFilter(headerMenu_IE, "PRIMARY");
      break;

    case "au":
    case "en_AU":
      footerMenu = menuFilter(footerMenu_AU, "MAIN_FOOTER");
      headerMenu = menuFilter(headerMenu_AU, "PRIMARY");
      break;

    case "can":
    case "ca":
    case "en_CA":
      footerMenu = menuFilter(footerMenu_CA, "MAIN_FOOTER");
      headerMenu = menuFilter(headerMenu_CA, "PRIMARY");
      break;

    case "za":
    case "en_ZA":
      footerMenu = menuFilter(footerMenu_ZA, "MAIN_FOOTER");
      headerMenu = menuFilter(headerMenu_ZA, "PRIMARY");
      break;

    case "en":
    case "eng":
    case "en_GB":
    default:
      footerMenu = menuFilter(defaultFooterMenu, "MAIN_FOOTER");
      headerMenu = menuFilter(defaultHeaderMenu, "PRIMARY");
  }

  const parentPackInfoFile =
    genericDetails?.parentInfo?.parentInfo?.infoPackFile?.mediaItemUrl ?? "";
  const globalFileLinks = {
    parentPack: parentPackInfoFile
  };

  const headerMenuItems = useMemo(() => headerMenu, [headerMenu]);

  return (
    <>
      <Nav
        headerContainerClass={headerContainerClass}
        site={site}
        headerMenu={headerMenuItems}
        enquireNow={enquireNow}
        applyNow={applyNow}
        brandingOpts={brandingOpts}
        globalFileLinks={globalFileLinks}
        enquireLabel={enquireLabel}
        applyLabel={applyLabel}
        isGroupPage={isGroupPage}
      />
      <div className="relative z-30  pt-16 planet:gvi-dark-gray-bg bg-primary-bg">
        {bgImage && (
          <div
            style={{ backgroundImage: `url(${bgImage})` }}
            className="absolute grid w-full h-full bg-top bg-no-repeat bg-cover background-image "
          />
        )}

        <div className="relative">
          <div
            className={containerClass ?? "container mx-auto py-8 px-2 bg-white"}
          >
            {children}
          </div>
        </div>
      </div>
      {footerShown && (
        <Footer
          {...additionalFooterOpts}
          site={site}
          footerMenu={footerMenu}
          brandingOpts={brandingOpts}
          globalFileLinks={globalFileLinks}
          enquireNow={enquireNow}
          applyNow={applyNow}
          programPrice={programPrice}
          programType={programType}
          showProgramPrice={showProgramPrice}
          showPricingStickyWidget={showPricingStickyWidget}
          showPromotion={showPromotion}
          programPromotion={programPromotion}
          updateOnScroll={updateOnScroll}
          stickyFooterDetails={stickyFooterDetails}
          showMobileEnquireNow={showMobileEnquireNow}
          showMobileApplyNow={showMobileApplyNow}
          showMobileStickyFooter={showMobileStickyFooter}
          isGroupPage={isGroupPage}
          showGrantsBanner={showGrantsBanner}
        />
      )}
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired
};

export default Layout;
